import { AdapterRegistry } from '../adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { DatabricksLinkedService } from 'app/model/linkedservice/databricks.linkedservice';
import { LinkedServiceAdapter } from './linkedservice.adapter';
import { IModel } from 'app/model/base/annotation';
import { Model } from 'app/model/backendmodel';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from './azure-key-vault.util';
import { AuthenticationType, ReferenceAdapter, Credential, CredentialTypeOptions, ITridentResource } from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DatabricksLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                domain: '*',
                clusterId: '*',
                accessToken: 'secret',
                encryptedCredential: '*',
                credential: '*',
                workspaceResourceId: '*'
            }
        },
        parameters: '*'
    }
})
export class DatabricksLinkedServiceAdapter extends LinkedServiceAdapter<DatabricksLinkedService> {
    protected supportFirstClassParameterExperience = true;

    public async postToModel(model: DatabricksLinkedService): Promise<void> {
        let finalAuthType: AuthenticationType = model.authenticationMethod;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = AuthenticationType.UserAuth;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = AuthenticationType.UserAssignedManagedIdentity;
            }
        }
        model.authenticationMethod = finalAuthType;
    }

    protected modelConstructor(): new () => DatabricksLinkedService {
        return DatabricksLinkedService;
    }

    protected model(): IModel {
        return DatabricksLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: DatabricksLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        let credential;
        let workspaceResourceId;
        let accessToken;

        const properties = this.properties(linkedServiceResource) as Model.DatabricksLinkedService;
        properties.type = this.model().name;
        if ((linkedService.authenticationMethod === AuthenticationType.UserAssignedManagedIdentity || linkedService.authenticationMethod === AuthenticationType.UserAuth) && linkedService.credential) {
            credential = ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
        }
        if (linkedService.authenticationMethod === AuthenticationType.AccessToken) {
            accessToken = parameterizablePasswordOrAKVtoSecretBase(linkedService, DatabricksLinkedService.ACCESS_TOKEN, this.context);
        } else {
            workspaceResourceId = this.valueToResource(linkedService, DatabricksLinkedService.WORKSPACE_RESOURCE_ID);
        }

        properties.typeProperties = {
            domain: this.valueToResource(linkedService, DatabricksLinkedService.DOMAIN),
            clusterId: this.valueToResource(linkedService, DatabricksLinkedService.CLUSTER_ID),
            accessToken,
            credential,
            workspaceResourceId
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: DatabricksLinkedService): Promise<DatabricksLinkedService> {
        const properties = linkedServiceResource.properties as Model.DatabricksLinkedService;
        const typeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, DatabricksLinkedService.DOMAIN, typeProperties.domain);
        ValueAdapter.toModel(linkedService, DatabricksLinkedService.CLUSTER_ID, typeProperties.clusterId);

        linkedService.manualSelected = Boolean(linkedService.clusterId) || ValueAdapter.isTemplated(linkedService, DatabricksLinkedService.CLUSTER_ID);
        linkedService.credential = undefined;
        if (typeProperties.workspaceResourceId) {
            ValueAdapter.toModel(linkedService, DatabricksLinkedService.WORKSPACE_RESOURCE_ID, typeProperties.workspaceResourceId);
            if (typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
            } else {
                linkedService.authenticationMethod = AuthenticationType.ManagedServiceIdentity;
            }
        } else {
            parameterizableSecretBaseToModel(linkedService, DatabricksLinkedService.ACCESS_TOKEN, typeProperties.accessToken, this.context);
            linkedService.authenticationMethod = AuthenticationType.AccessToken;
        }
        return linkedService;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.DatabricksLinkedService> => {
        try {
            const parsedConnection = JSON.parse(tridentResource.connectionDetails);
            const { host, httpPath } = JSON.parse(parsedConnection.extensionDataSourcePath);
            return { domain: host, clusterId: httpPath };
        } catch (error) {
            return {};
        }
    }
}
